// React imports
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Components
import Button from '../components/Button.tsx';

// Contextes
import { useQuestions } from '../utils/QuestionsContexte.tsx';
import { useInTheGame } from '../utils/InTheGameContexte.tsx';
import { useGame } from '../utils/GameContexte.tsx';

// Data
import { questionsData } from '../data/questions.ts';

import gsap from 'gsap';

function Jeu() {
    const { id } = useParams();
    const navigate = useNavigate();

    // Used questions saved in the context
    const { markTeamQuestionAsUsed, teamUsedQuestions } = useQuestions();

    // Toggle Game
    const { toggleGame } = useInTheGame();
    useEffect(() => {
        toggleGame(true);
    }, []);

    // Game contexte variables
    const { totalPlayers, equipesNoms, activePlayer } = useGame();

    // Array of themes
    const themes = [
        ['La maladie', 'red'],
        ['Les modes de transmission', 'violet'],
        ['La prise en charge', 'blue'],
        ['Le dépistage', 'yellow'],
        ['La guérison', 'green'],
    ];

    // Array of difficulty levels
    const difficulty = [1, 2, 3, 4];

    // Variable to store the availability of each difficulty level
    const [isDifficultyAvailable, setIsDifficultyAvailable] = useState(
        new Array(difficulty.length).fill(true)
    );

    // Function to select a random theme and difficulty
    function selectRandomThemeAndDifficulty(): [string, number] {
        const randomThemeIndex = Math.floor(Math.random() * questionsData.length);
        const theme = questionsData[randomThemeIndex].theme;

        // Assurez-vous que questionsParDifficulte est défini avant de continuer
        const difficulties = questionsData[randomThemeIndex].questionsParDifficulte;
        if (!difficulties) {
            throw new Error('No difficulties available for the selected theme');
        }

        const randomDifficultyIndex = Math.floor(Math.random() * difficulties.length);
        const difficulty = difficulties[randomDifficultyIndex].difficulte;

        return [theme, difficulty]; // S'assure que le premier élément est un string et le second un number
    }

    // Stocker le thème et la difficulté sélectionnés
    const [selectedTheme, setSelectedTheme] = useState<string>('');
    const [selectedDifficulty, setSelectedDifficulty] = useState<number>(0);

    // Function to handle the selection of the theme
    const handleThemeSelection = (index: number) => {
        // Assurez-vous que le thème est toujours une chaîne lors de la définition
        setSelectedTheme(themes[index][0].toString());
    };

    // Function to handle the selection of the difficulty
    const handleDifficultySelection = (level: number) => {
        setSelectedDifficulty(level);
    };

    // Function to check if a question is available for the selected theme and difficulty
    const isAvailable = (theme: string, difficulty: number): boolean => {
        const availableQuestions = questionsData
            .filter((questionTheme) => questionTheme.theme === theme)
            .flatMap((questionTheme) => questionTheme.questionsParDifficulte ?? [])
            .find((diff) => diff.difficulte === difficulty)
            ?.questions.filter(
                (question) =>
                    !teamUsedQuestions[activePlayer]?.[theme]?.[difficulty]?.has(question.id)
            );

        // Utilisation de `!!` pour forcer la conversion à un booléen
        return !!availableQuestions && availableQuestions.length > 0;
    };

    // Update the availability of the difficulty levels when the theme or difficulty changes
    useEffect(() => {
        // Calculer la disponibilité pour chaque niveau de difficulté
        const availability = difficulty.map((_, index) => isAvailable(selectedTheme, index + 1));
        setIsDifficultyAvailable(availability);
    }, [selectedDifficulty, selectedTheme]);

    // Create an array of buttons for the themes
    const buttonsArray = themes.map((theme, index) => {
        return (
            <Button
                scene='training'
                animationClass='animation-item'
                colorName={theme[1]}
                isSelected={selectedTheme === theme[0]}
                onClick={() => handleThemeSelection(index)}
                key={`{${index}--buttonTheme}`}
            >
                {theme[0]}
            </Button>
        );
    });

    // Create an array of buttons for the difficulty levels
    const difficultyButtonArray = difficulty.map((number, index) => {
        return (
            <Button
                scene='difficulty'
                colorName='blue'
                isSelected={selectedDifficulty === number}
                key={`{${index}--buttonDifficulty}`}
                disabled={!isDifficultyAvailable[index]}
                onClick={() => handleDifficultySelection(number)}
            >
                {number}
            </Button>
        );
    });

    // Function to select a random question based on the selected theme and difficulty
    const selectRandomQuestion = () => {
        // Filtrez les questions basées sur le thème et la difficulté
        const filteredQuestions = questionsData
            .filter((qt) => qt.theme === selectedTheme)
            .flatMap((qt) => qt.questionsParDifficulte ?? [])
            .find((qpd) => qpd.difficulte === selectedDifficulty)
            ?.questions.filter(
                (q) =>
                    !teamUsedQuestions[activePlayer]?.[selectedTheme]?.[selectedDifficulty]?.has(
                        q.id
                    )
            );

        // Sélectionnez aléatoirement une question parmi les filtrées
        if (filteredQuestions && filteredQuestions.length > 0) {
            const randomIndex = Math.floor(Math.random() * filteredQuestions.length);
            return filteredQuestions[randomIndex];
        }
        return null;
    };

    // Function to select a random question and navigate to the question page
    const handleStartClick = () => {
        if (selectedTheme !== '' && selectedDifficulty !== 0) {
            const question = selectRandomQuestion();
            if (question) {
                markTeamQuestionAsUsed(
                    activePlayer,
                    selectedTheme,
                    selectedDifficulty,
                    question.id
                );
                const tl = gsap.timeline();

                // Animer l'échelle du texte
                tl.fromTo(
                    document.querySelectorAll('.animation-item'),
                    {
                        xPercent: 0,
                        autoAlpha: 1,
                    },
                    {
                        autoAlpha: 0,
                        xPercent: 120,
                        duration: 1,
                        stagger: 0.1,
                        ease: 'power2.out',
                        onComplete: () => {
                            navigate(`/${id}/jeu/questions`, {
                                state: {
                                    question: question, // Envoyez l'objet question complet
                                    theme: selectedTheme,
                                    difficulty: selectedDifficulty,
                                },
                            });
                        },
                    }
                );
                tl.fromTo(
                    document.querySelectorAll('.jeuPoints--animation'),
                    {
                        yPercent: 0,
                        autoAlpha: 1,
                    },
                    {
                        autoAlpha: 0,
                        yPercent: -200,
                        duration: 1,
                        stagger: 0.1,
                        ease: 'power2.out',
                    },
                    '<'
                );
                tl.fromTo(
                    document.querySelectorAll('.animation-item-top'),
                    {
                        yPercent: 0,
                        autoAlpha: 1,
                    },
                    {
                        autoAlpha: 0,
                        yPercent: -200,
                        duration: 1,
                        stagger: 0.1,
                        ease: 'power2.out',
                    },
                    '<'
                );
            } else {
                // Gérer le cas où il n'y a plus de questions disponibles
                // console.log('Aucune question disponible');
            }
        }
    };

    useEffect(() => {
        if (id === 'expert') {
            const [randomTheme, randomDifficulty] = selectRandomThemeAndDifficulty();
            setSelectedTheme(randomTheme);
            setSelectedDifficulty(randomDifficulty);
        }
    }, [id]);

    useEffect(() => {
        const tl = gsap.timeline();

        // Animer le texte
        tl.fromTo(
            document.querySelectorAll('.animation-item'),
            {
                xPercent: 120,
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                xPercent: 0,
                duration: 1,
                stagger: 0.1,
                ease: 'power2.out',
            }
        );
        tl.fromTo(
            document.querySelectorAll('.animation-item-top'),
            {
                xPercent: 120,
                autoAlpha: 0,
            },
            {
                autoAlpha: 1,
                xPercent: 0,
                duration: 1,
                stagger: 0.1,
                ease: 'power2.out',
            },
            '<'
        );
    }, []);

    return (
        <>
            {totalPlayers > 1 && (
                <p
                    className={`${
                        id === 'classique' ? '' : 'jeuText--width'
                    } text-page-semib text-page-semib--darkBlueBig`}
                >
                    Equipe {equipesNoms[activePlayer]}
                    <span> , à votre tour</span>
                </p>
            )}

            <div
                className='jeuButtons'
                style={{
                    marginTop: id === 'expert' && totalPlayers === 1 ? '6rem' : '0',
                }}
            >
                <p
                    className={`text-page-light text-page-light--darkBlue ${
                        totalPlayers > 1 ? '' : 'jeuText--margin'
                    } animation-item-top`}
                >
                    {`${id === 'expert' ? 'Thémathique' : 'Choisissez la thématique'}`}
                </p>
                {id === 'expert' ? (
                    <div className='animation-item'>
                        <Button
                            scene='training'
                            colorName={`${
                                selectedTheme === 'La maladie'
                                    ? 'red'
                                    : selectedTheme === 'Les modes de transmission'
                                    ? 'violet'
                                    : selectedTheme === 'La prise en charge'
                                    ? 'blue'
                                    : selectedTheme === 'Le dépistage'
                                    ? 'yellow'
                                    : 'green'
                            }`}
                            isSelected={true}
                            key={selectedTheme}
                        >
                            {selectedTheme}
                        </Button>
                    </div>
                ) : (
                    buttonsArray
                )}
            </div>

            <div className='jeuButtonList'>
                <p className='text-page-light text-page-light--darkBlue animation-item-top'>
                    {id === 'expert' ? 'Niveau de dfficulté' : 'Choisissez le niveau de difficulté'}
                </p>
                <div className='difficultyButtonsList animation-item'>
                    {id === 'expert' ? (
                        <Button
                            scene='difficulty'
                            colorName='blue'
                            isSelected={true}
                            key={selectedDifficulty}
                        >
                            {selectedDifficulty}
                        </Button>
                    ) : (
                        difficultyButtonArray
                    )}
                </div>
                {selectedDifficulty && (
                    <div className='text-infos-light text-infos-light--blue jeuPoints jeuPoints--animation'>{`Cette question rapportera ${selectedDifficulty} ${
                        selectedDifficulty === 1 ? 'point' : 'points'
                    }`}</div>
                )}
            </div>

            <Button
                scene='start'
                colorName='red'
                isSelected={false}
                disabled={selectedTheme === '' || selectedDifficulty === 0} // Le bouton est désactivé si aucun thème ou difficulté n'est sélectionné
                onClick={() => {
                    handleStartClick();
                }}
                animationClass='animation-item'
            >
                Voir la question
            </Button>
        </>
    );
}

export default Jeu;
